import {parseAdminDocMarkdown, resolveAdminDocMetadata} from "~/utilities/adminDocs/parseAdminDocMarkdown";

const adminDocModules = import.meta.glob<string>("../../routes/**/a/_adminScaffold/*/-adminDoc.md", {
    query: "?raw",
    import: "default",
    eager: true,
});

const ADMIN_DOC_PATH_REGEX = /\/a\/_adminScaffold\/([^/]+)\/-adminDoc\.md$/;

export type AdminModuleDocDefinition = {
    slug: string;
    title: string;
    sidebarLabel: string;
    adminRoute: `/a/${string}`;
    summary: string;
    markdown: string;
    openApiPathPrefixes: string[];
    relatedOpenApiPathPrefixes: string[];
    /** Optional named API tabs; unused — reserved for future grouped API tabs. */
    apiReferenceGroups: Array<{label: string; pathPrefixes: string[]}>;
    requiredPlatformAccess?: string;
    includesWorkspaceDefinitionsReference?: boolean;
    /** Matches admin sidebar sections for the docs index. */
    section: string;
};

/** Preferred docs-index order — mirrors the admin sidebar. */
export const ADMIN_DOC_SECTION_ORDER = [
    "Identity & platform",
    "Products",
    "Developer tools",
    "Other",
] as const;

function extractSlugFromAdminDocPath(path: string): string | null {
    const match = path.match(ADMIN_DOC_PATH_REGEX);
    return match?.[1] ?? null;
}

function buildAdminModuleDocsRegistry(): Record<string, AdminModuleDocDefinition> {
    const registry: Record<string, AdminModuleDocDefinition> = {};

    for (const [filePath, rawContent] of Object.entries(adminDocModules)) {
        if (typeof rawContent !== "string") {
            continue;
        }

        const slug = extractSlugFromAdminDocPath(filePath);
        if (slug === null) {
            continue;
        }

        const parsed = parseAdminDocMarkdown(rawContent);
        const resolved = resolveAdminDocMetadata({
            slug,
            frontmatter: parsed.frontmatter,
            markdown: parsed.markdown,
        });

        registry[resolved.slug] = resolved;
    }

    if (Object.keys(registry).length === 0) {
        throw new Error("No admin module docs discovered. Expected files matching a/_adminScaffold/*/-adminDoc.md");
    }

    return registry;
}

export const ADMIN_MODULE_DOCS = buildAdminModuleDocsRegistry();

export type AdminModuleDocSlug = keyof typeof ADMIN_MODULE_DOCS;

export const ADMIN_MODULE_DOC_LIST = Object.values(ADMIN_MODULE_DOCS).sort((left, right) => left.title.localeCompare(right.title));

export function getAdminModuleDoc(slug: string): AdminModuleDocDefinition | null {
    if (Object.hasOwn(ADMIN_MODULE_DOCS, slug) !== true) {
        return null;
    }
    return ADMIN_MODULE_DOCS[slug] ?? null;
}

export function getAdminModuleDocForRoutePath(routePath: string): AdminModuleDocDefinition | null {
    const normalizedPath = routePath.replace(/\/$/, "");
    const matchingDoc = ADMIN_MODULE_DOC_LIST.find((moduleDoc) => {
        const normalizedAdminRoute = moduleDoc.adminRoute.replace(/\/$/, "");
        return normalizedPath === normalizedAdminRoute || normalizedPath.startsWith(`${normalizedAdminRoute}/`);
    });
    return matchingDoc ?? null;
}
